import createSelDriver from './createDriver';
import { getFeature } from './registry';
import { isTourDismissed, markTourDismissed, clearTourDismissed } from './storage';

let activeDriver = null;
let pendingTourComplete = null;

export function isTourActive() {
  return Boolean(activeDriver);
}

/** Encerra tour ativo sem encadear onComplete (ex.: mudança de situação). */
export function destroyActiveTour() {
  if (!activeDriver) return;
  pendingTourComplete = null;
  try {
    activeDriver.destroy();
  } catch (error) {
    // ignore
  }
  activeDriver = null;
}

function tourStepsValidos(tour, ctx = {}) {
  const steps = typeof tour.steps === 'function' ? tour.steps(ctx) : tour.steps || [];
  return steps.filter(step => {
    if (!step.element) return true;
    // Steps that opt into waitForElement are trusted to reveal their own target later (e.g.
    // a previous step's onNextClick expands a panel) — driver.js's own MutationObserver-based
    // wait handles them once handed off; don't pre-reject them here just because the element
    // isn't in the DOM yet at this one-shot check.
    if (step.waitForElement) return true;
    return Boolean(document.querySelector(step.element));
  });
}

function tourPertenceASituacao(tour, ctx = {}) {
  if (tour.situation == null) return true;
  if (ctx.situation == null || ctx.situation === '') return false;
  return Number(tour.situation) === Number(ctx.situation);
}

function tourPodeRodarAgora(tour, ctx = {}) {
  if (!tour || tour.kind === 'hint') return false;
  // Gate central: tour com `situation` só roda na situação atual do pregão.
  if (!tourPertenceASituacao(tour, ctx)) return false;
  if (typeof tour.canStart === 'function' && !tour.canStart(ctx)) return false;
  return tourStepsValidos(tour, ctx).length > 0;
}

export function podeExibirTour(tourId, userId, ctx = {}) {
  const tour = getFeature(tourId);
  if (!tour || tour.kind === 'hint') return false;
  if (userId == null) return false;
  if (isTourDismissed(userId, tour.id, tour.version)) return false;
  return tourPodeRodarAgora(tour, ctx);
}

export function podeReabrirTour(tourId, userId, ctx = {}) {
  const tour = getFeature(tourId);
  if (!tour || tour.kind === 'hint' || userId == null) return false;
  return tourPodeRodarAgora(tour, ctx);
}

export function marcarTourVisto(userId, tourId) {
  const tour = getFeature(tourId);
  if (!tour || userId == null) return;
  markTourDismissed(userId, tour.id, tour.version);
}

function driveTour(tour, userId, ctx = {}, { onComplete } = {}) {
  const stepsValidos = tourStepsValidos(tour, ctx);
  if (!stepsValidos.length) return false;

  if (activeDriver) {
    try {
      activeDriver.destroy();
    } catch (error) {
      // ignore
    }
    activeDriver = null;
  }

  const dismiss = () => {
    marcarTourVisto(userId, tour.id);
  };

  pendingTourComplete = typeof onComplete === 'function' ? onComplete : null;

  activeDriver = createSelDriver({
    steps: stepsValidos,
    onDestroyStarted: (_element, _step, { driver: driverObj }) => {
      dismiss();
      driverObj.destroy();
    },
    onDestroyed: () => {
      activeDriver = null;
      const cb = pendingTourComplete;
      pendingTourComplete = null;
      if (cb) {
        Promise.resolve().then(() => {
          try {
            cb();
          } catch (error) {
            // ignore
          }
        });
      }
    },
  });

  activeDriver.drive();
  return true;
}

export function startTour(tourId, userId, ctx = {}, options = {}) {
  if (!podeExibirTour(tourId, userId, ctx)) return false;
  return driveTour(getFeature(tourId), userId, ctx, options);
}

export function reabrirTour(tourId, userId, ctx = {}) {
  const tour = getFeature(tourId);
  if (!tour || tour.kind === 'hint' || userId == null) return false;
  if (!tourPodeRodarAgora(tour, ctx)) return false;

  clearTourDismissed(userId, tour.id, tour.version);
  // Reabertura manual não encadeia a fila do contexto.
  return driveTour(tour, userId, ctx);
}
